import { create } from "zustand";
import { devtools } from "zustand/middleware";

export interface GuideFilter {
  selectedCategoriesFilter: string[];
  selectedPropertiesFilter: string[];
  searchGuideTerm: string;
}

interface GuideFiltersState {
  // State
  filters: GuideFilter;

  // Actions
  setFilters: (newFilters: Partial<GuideFilter>) => void;
  clearFilters: () => void;
  setSearchTerm: (term: string) => void;
  setCategoryFilters: (categories: string[]) => void;
  setPropertyFilters: (properties: string[]) => void;
}

const initialFilters: GuideFilter = {
  selectedCategoriesFilter: [],
  selectedPropertiesFilter: [],
  searchGuideTerm: "",
};

// DevTools configuration options
const devToolsOptions = {
  // Name of the store in Redux DevTools (helps when you have multiple stores)
  name: "GuideFilters",

  // Enable/disable DevTools based on environment (optional)
  // You could use process.env.NODE_ENV !== 'production' here
  enabled: true,

  // Custom anonymous action type (optional)
  anonymousActionType: "UNKNOWN_ACTION",

  // Serialize options (optional - used for handling complex objects)
  serialize: {
    // Options for handling circular references
    options: true,
  },
};

// State persists across React Router navigations (module singleton) and
// resets naturally on page refresh — no localStorage needed.
export const useGuideFiltersStore = create<GuideFiltersState>()(
  // DevTools should be the outermost middleware
  devtools(
    (set) => ({
      // Initial state
      filters: initialFilters,

      // Actions
      setFilters: (newFilters) =>
        // The third parameter to set allows us to name the action for DevTools
        set(
          (state) => ({
            filters: { ...state.filters, ...newFilters },
          }),
          false,
          { type: "guide-filters/setFilters", payload: newFilters }
        ),

      clearFilters: () =>
        set({ filters: initialFilters }, false, "guide-filters/clearFilters"),

      setSearchTerm: (searchGuideTerm) =>
        set(
          (state) => ({
            filters: { ...state.filters, searchGuideTerm },
          }),
          false,
          {
            type: "guide-filters/setSearchTerm",
            payload: searchGuideTerm,
          }
        ),

      setCategoryFilters: (selectedCategoriesFilter) =>
        set(
          (state) => ({
            filters: { ...state.filters, selectedCategoriesFilter },
          }),
          false,
          {
            type: "guide-filters/setCategoryFilters",
            payload: selectedCategoriesFilter,
          }
        ),

      setPropertyFilters: (selectedPropertiesFilter) =>
        set(
          (state) => ({
            filters: { ...state.filters, selectedPropertiesFilter },
          }),
          false,
          {
            type: "guide-filters/setPropertyFilters",
            payload: selectedPropertiesFilter,
          }
        ),
    }),
    // Pass DevTools options as the second parameter to devtools
    devToolsOptions
  )
);
